<template>
    <form-group :horizontal="horizontal" :label="label" :label-classes="[{ required }, labelClasses]">
        <textarea
            ref="textarea"
            :autofocus="autofocus"
            :class="{ 'is-invalid': invalidFeedback }"
            :disabled="disabled"
            :name="name"
            :placeholder="placeholder"
            :rows="rows"
            :style="{ maxHeight: maxRows ? `${maxRows * 24}px` : 'none' }"
            :value="modelValue"
            class="form-control !resize-none"
            @input="updateValue"
            @keyup="$emit('keyup', $event)"
        />
        <c-form-text v-if="description">
            <div v-if="description" class="text-muted">
                {{ description }}
            </div>
        </c-form-text>
        <c-form-feedback :valid="!invalidFeedback"
            ><small class="text-danger">{{ invalidFeedback }}</small></c-form-feedback
        >
    </form-group>
</template>

<script setup>
import FormGroup from '@/Components/FormGroup.vue';
import { useTextareaAutosize } from '@/Utils/UseTextareaAutosize';
import { nextTick, watch } from 'vue';

const props = defineProps({
    name: String,
    label: String,
    labelClasses: [Object, Array, String],
    type: {
        type: String,
        default: 'text',
    },
    invalidFeedback: {
        type: String,
        default: '',
    },
    autocomplete: String,
    disabled: {
        type: Boolean,
        default: false,
    },
    modelValue: String,
    horizontal: {
        default: true,
    },
    autofocus: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    placeholder: {
        type: String,
        default: '',
    },
    description: {
        type: String,
        default: '',
    },
    rows: {
        type: Number,
        default: 5,
    },
    maxRows: Number,
    autoResize: {
        type: Boolean,
        default: true,
    },
});

const { textarea, triggerResize } = useTextareaAutosize({
    styleProp: 'minHeight',
    maxHeight: props.maxRows ? props.maxRows * 24 : undefined,
});

const emit = defineEmits(['update:modelValue', 'keyup']);

// The autosize composable watches its own `input` ref, which this controlled
// textarea (bound via :value/@input) never updates — so resize on every edit
// AND when modelValue is set externally (prefill, programmatic change).
watch(
    () => props.modelValue,
    () => {
        if (props.autoResize) {
            nextTick(triggerResize);
        }
    },
);

function updateValue(event) {
    emit('update:modelValue', event.target.value.trim());

    if (props.autoResize) {
        triggerResize();
    }
}
</script>
<style scoped>
:deep(textarea) {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

:deep(textarea::-webkit-scrollbar) {
    display: none;
}
</style>
